import { useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { useTranslation } from '@aimsos/i18n';
import { Button } from '@aimsos/ui';

import { OpenTasksLink } from './HitlGateCardParts.jsx';

/**
 * Body for the document-pick gate (`mode: 'pick'`): choose a document from
 * AIMS Drive or upload a local file through the composer pipeline. Owns the
 * state only this mode uses: `attaching` and the hidden file input.
 *
 * @param {object} props
 * @param {{ render?: object }} props.card
 * @param {ReturnType<import('../../../hooks/useHitlGateDecision.js').useHitlGateDecision>} props.decision
 * @param {((card: object) => void)|null} [props.onPickDocument]  Host opens the
 *   AIMS Drive picker and resolves the gate with the chosen `document_uuid`.
 * @param {((card: object, file: File) => Promise<boolean>)|null} [props.onAttachLocalFile]
 *   Host uploads a local file and resolves the gate with the resulting
 *   `file_uuid`. When omitted the local upload control is hidden (Drive-only).
 * @param {(() => void)|null} [props.onOpenTasks]
 * @param {string} [props.translationNamespace='chat']
 */
function PickGateBody({
  card,
  decision,
  onPickDocument = null,
  onAttachLocalFile = null,
  onOpenTasks = null,
  translationNamespace = 'chat',
}) {
  const { t } = useTranslation(translationNamespace);
  // Uploading + resolving a local file attachment (disables both pick
  // controls and shows progress until the host clears the card).
  const [attaching, setAttaching] = useState(false);
  const fileInputRef = useRef(null);

  const render = card?.render ?? {};

  // Local-attach: upload the chosen file through the composer pipeline and let
  // the host resolve the gate with the resulting file_uuid. Mirrors the Drive
  // pick — the host clears the card on success, so no chosen-file chip is kept.
  const onLocalFileChange = async (event) => {
    const file = event.target.files?.[0];
    // Reset the input so re-choosing the same file still fires onChange.
    event.target.value = '';
    if (!file || !onAttachLocalFile) return;
    setAttaching(true);
    try {
      await onAttachLocalFile(card, file);
    } finally {
      setAttaching(false);
    }
  };

  const pickBusy = decision.resolving || attaching;

  return (
    <>
      <p className="text-sm whitespace-pre-wrap">
        {render.prompt || render.summary || t('chat.hitl.pickPrompt')}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => onPickDocument?.(card)} disabled={pickBusy}>
          {t('chat.hitl.chooseFromDrive')}
        </Button>
        {onAttachLocalFile ? (
          <>
            <Button
              variant="secondary"
              leftIcon={Upload}
              loading={attaching}
              onClick={() => fileInputRef.current?.click()}
              disabled={pickBusy}
            >
              {attaching ? t('chat.hitl.uploading') : t('chat.hitl.uploadFile')}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={onLocalFileChange}
              aria-hidden
              tabIndex={-1}
            />
          </>
        ) : null}
        {onOpenTasks ? (
          <div className="ml-auto">
            <OpenTasksLink onOpenTasks={onOpenTasks} label={t('chat.hitl.viewInTasks')} />
          </div>
        ) : null}
      </div>
    </>
  );
}

export default PickGateBody;
